const MIN_VIDEO_COUNT = 3;
const VIDEO_SLOTS = ['env', 'left', 'right'];

function ensureEpisode(stats, episodeKey) {
    if (!stats.has(episodeKey)) {
        stats.set(episodeKey, {
            mcapCount: 0,
            mp4Count: 0,
            videoSlots: {env: 0, left: 0, right: 0},
            videoPaths: [],
            urdfCount: 0,
        });
    }
    return stats.get(episodeKey);
}

function resolveVideoSlot(fileName) {
    const normalized = String(fileName || '').toLowerCase();
    if (normalized.includes('left')) return 'left';
    if (normalized.includes('right')) return 'right';
    return 'env';
}

function primarySortKey(fileName, slot) {
    const baseName = String(fileName || '').split('/').pop().toLowerCase();
    const stem = baseName.replace(/\.[^.]*$/, '');
    const exactPrimary = {
        env: new Set(['high', 'env']),
        left: new Set(['left']),
        right: new Set(['right']),
    };
    return [exactPrimary[slot]?.has(stem) ? 0 : 1, baseName];
}

function comparePrimaryCandidates(slot) {
    return (left, right) => {
        const leftKey = primarySortKey(left, slot);
        const rightKey = primarySortKey(right, slot);
        if (leftKey[0] !== rightKey[0]) return leftKey[0] - rightKey[0];
        if (leftKey[1] < rightKey[1]) return -1;
        if (leftKey[1] > rightKey[1]) return 1;
        return 0;
    };
}

function selectPrimaryVideoPaths(videoPaths) {
    const grouped = {env: [], left: [], right: []};
    (videoPaths || []).forEach((path) => {
        if (!String(path || '').endsWith('.mp4')) return;
        const fileName = String(path).split('/').pop();
        grouped[resolveVideoSlot(fileName)].push(path);
    });
    return VIDEO_SLOTS.reduce((selected, slot) => {
        const candidates = grouped[slot].slice().sort(comparePrimaryCandidates(slot));
        if (candidates.length) selected[slot] = candidates[0];
        return selected;
    }, {});
}

function validateMcapMp4(files) {
    const errors = [];
    const stats = new Map();

    const addError = (message) => {
        errors.push(message);
    };

    (files || []).forEach(({relative_path}) => {
        if (!relative_path) return;

        if (relative_path.includes('/')) {
            const parts = relative_path.split('/');
            if (parts.length !== 2) {
                addError(`Nested folders are not allowed: ${relative_path}`);
                return;
            }
            const [folderName, fileName] = parts;
            if (!folderName || !fileName) {
                addError(`Invalid path: ${relative_path}`);
                return;
            }
            if (folderName.endsWith('.mcap')) {
                addError(`Video folder name must not end with .mcap: ${folderName}`);
                return;
            }
            if (fileName.endsWith('.urdf')) {
                ensureEpisode(stats, folderName).urdfCount += 1;
                return;
            }
            if (!fileName.endsWith('.mp4')) {
                addError(`Only .mp4 and .urdf files are allowed inside episode folders (extensions must be lowercase): ${relative_path}`);
                return;
            }
            const episodeStats = ensureEpisode(stats, folderName);
            episodeStats.mp4Count += 1;
            episodeStats.videoSlots[resolveVideoSlot(fileName)] += 1;
            episodeStats.videoPaths.push(relative_path);
            return;
        }

        if (relative_path.endsWith('.mcap')) {
            const episodeKey = relative_path.replace(/\.mcap$/, '');
            if (!episodeKey) {
                addError(`Invalid .mcap filename at root: ${relative_path}`);
                return;
            }
            const episodeStats = ensureEpisode(stats, episodeKey);
            episodeStats.mcapCount += 1;
            return;
        }

        addError(`Only .mcap files are allowed at root: ${relative_path}`);
    });

    const episodeKeys = Array.from(stats.keys()).sort();
    if (!episodeKeys.length) {
        addError('No valid episodes found (expect {episode}.mcap at root and at least 3 .mp4 files under {episode}/).');
    }

    episodeKeys.forEach((episodeKey) => {
        const {mcapCount, mp4Count, videoPaths, urdfCount} = stats.get(episodeKey);
        if (urdfCount > 1) {
            addError(`Episode ${episodeKey} may contain at most one .urdf file.`);
        }
        if (mcapCount !== 1) {
            addError(`Episode ${episodeKey} must have exactly 1 .mcap file (found ${mcapCount}).`);
        }
        if (mp4Count < MIN_VIDEO_COUNT) {
            addError(`Episode ${episodeKey} must have at least ${MIN_VIDEO_COUNT} .mp4 files (found ${mp4Count}).`);
        } else {
            const primaryVideos = selectPrimaryVideoPaths(videoPaths);
            const missingSlots = VIDEO_SLOTS.filter((slot) => !primaryVideos[slot]);
            if (missingSlots.length) {
                addError(
                    `Episode ${episodeKey} must include primary env/high, left, and right MP4s `
                    + `(missing: ${missingSlots.join(', ')}). Use one filename containing "left", `
                    + `one containing "right", and one containing neither.`,
                );
            }
        }
    });

    return {episodeKeys, errors};
}

export default validateMcapMp4;
